import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import useAuth from "./hooks/useAuth";
import AuthLayout from "./layouts/auth";
import DashboardLayout from "./layouts/dashboard";
import ProtectedLayout from "./routes/ProtectedLayout";
import LocalStorageService from "./services/LocalStorage/LocalStorageService";
import { useAppSelector } from "./store/store";
import { LSKeyAccessToken } from "./lib/LocalStorageConstants";
import { tenant } from "./lib/tenant";
import { OrganizationService } from "./services/Organization";

const HomePage = lazy(() => import("./pages/home"));
const LoginPage = lazy(() => import("./pages/login"));
const RegisterPage = lazy(() => import("./pages/register"));
const OrderCardPage = lazy(() => import("./pages/order-card"));
const CardsPage = lazy(() => import("./pages/cards"));
const ProfilePage = lazy(() => import("./pages/profile"));
const ForgotPasswordPage = lazy(() => import("./pages/forgot-password"));
const KycPage = lazy(() => import("./pages/kyc"));
const ReferralPage = lazy(() => import("./pages/referral"));

function App() {
	const { checkAuth } = useAuth();
	const isLoading = useAppSelector((state) => state.auth.isLoading);

	// Base organization settings, fetched once when the app opens.
	// Nothing consumes the response yet — features will read it from the
	// RTK Query cache via useGetOrganizationInfoQuery.
	OrganizationService.useGetOrganizationInfoQuery();

	const nonAuthRoutes = [
		{ path: "/sign-in", element: <LoginPage /> },
		{ path: "/sign-up", element: <RegisterPage /> },
		{ path: "/forgot-password", element: <ForgotPasswordPage /> },
	];

	const protectedRoutes = [
		{ path: "/", element: <HomePage /> },
		{ path: "/cards", element: <CardsPage /> },
		{ path: "/order-card", element: <OrderCardPage /> },
		{ path: "/profile", element: <ProfilePage /> },
		{ path: "/kyc", element: <KycPage /> },
		{ path: "/referral", element: <ReferralPage /> },
	];

	useEffect(() => {
		// Apply tenant brand to <title> at runtime.
		// (favicon is configured via public/favicon.svg, which each tenant overrides.)
		document.title = tenant.brand;
	}, []);

	useEffect(() => {
		const accessToken = LocalStorageService.get(LSKeyAccessToken);
		const pendingVerification = localStorage.getItem("register_verify_state");
		if (accessToken && !pendingVerification) {
			checkAuth();
		}
	}, [checkAuth]);

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
			</div>
		);
	}

	return (
		<div className="App">
			<Suspense
				fallback={
					<div className="min-h-screen flex items-center justify-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
					</div>
				}
			>
				<Routes>
					<Route element={<AuthLayout />}>
						{nonAuthRoutes.map((item) => (
							<Route
								key={item.path}
								path={item.path}
								element={item.element}
							/>
						))}
					</Route>

					<Route element={<ProtectedLayout />}>
						<Route element={<DashboardLayout />}>
							{protectedRoutes.map((item) => (
								<Route
									key={item.path}
									path={item.path}
									element={item.element}
								/>
							))}
						</Route>
					</Route>

					{/* Referral links use /signup?ref=CODE — the ref is captured
					    before the router mounts, here we just land on the form */}
					<Route path="/signup" element={<Navigate to="/sign-up" replace />} />

					<Route path="*" element={<div>404</div>} />
				</Routes>
			</Suspense>
		</div>
	);
}

export default App;
